import { useEffect, useState } from 'react';
import {
  RecommendPanel,
  Result as RecommendJob,
} from '@doda/common/src/interfaces/api/recommendPanel';
import { postRecommend } from '@doda/common/src/services/postRecommend';
import { RecommendGrid } from 'components/RecommendGrid';
import { useAppDispatch, useAppSelector } from '@doda/common/src/redux/hooks';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { ruijiRecommendGroupNoSelector } from '@doda/common/src/redux/features/abTest';
import { tl } from '@doda/common/src/utils/adobeAnalyticsUtil';
import styles from './index.module.scss';
import {
  measureClickRecommendFavorite,
  measureClickRecommendJobCardAdobe,
} from '../../logics/analytics';
import { useTab } from '../TabArea/hooks/useTab';
import { Tab } from '../../types/tab';
import { useRecommendVisibleAnalytics } from './hooks/useRecommendVisibleAnalytics';

/**
 * RecommendSectionのprops
 */
interface Props {
  jid: string;
  initialTab: Tab;
  wcnsId: string;
}

/**
 * 求人詳細のレコメンドMAX3種表示セクション
 * @param jid 求人ID
 */
export const RecommendSection = ({ jid, initialTab, wcnsId }: Props) => {
  const dispatch = useAppDispatch();
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const [recommendList, setRecommendList] = useState<RecommendPanel[]>([]);
  const { currentTabName } = useTab({ initialTab, jid, wcnsId });

  const isPatternARuijiRecommend =
    useAppSelector(ruijiRecommendGroupNoSelector) === 2;

  const createHandleClickRecommend =
    (effectName: string) => (recommendJob: RecommendJob, index: number) => {
      measureClickRecommendJobCardAdobe(
        isAdobeScript,
        recommendJob.recommendType,
        effectName,
        dispatch,
        String(index),
        currentTabName
      );
    };

  const handleClickFavorite = (recommendJob: RecommendJob, index: number) => {
    measureClickRecommendFavorite(
      isAdobeScript,
      recommendJob.recommendType,
      dispatch,
      String(index),
      currentTabName,
      recommendJob.itemNo
    );
  };

  const { refs } = useRecommendVisibleAnalytics({
    recommendList,
    currentTabName,
  });
  useEffect(() => {
    if (!jid) return;
    const jobDetailRecommendParams = isPatternARuijiRecommend
      ? createJobDetailRecommendParamsForABTest(jid)
      : createJobDetailRecommendParams(jid);

    const promises = jobDetailRecommendParams.map((params) =>
      postRecommend(params)
    );
    Promise.allSettled(promises).then((responses) => {
      responses.forEach((response) => {
        // データなしの場合は非表示
        if (response.status === 'rejected' || !response.value.data) return;
        setRecommendList((prevList) => [...prevList, response.value.data]);
      });
    });
  }, [jid, isPatternARuijiRecommend]);

  useEffect(() => {
    if (!isAdobeScript) return;
    // 類似レコメンドABテストAA計測
    // PV計測より前に発火しないように発火を遅らせる
    setTimeout(() => {
      tl(isPatternARuijiRecommend ? 'test_ruiji' : 'ori_ruiji', {});
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript]);

  if (!recommendList.length) return null;
  return (
    <section className={styles['jobSearchDetail-recommendSection']}>
      {recommendList.map((recommend, index) => {
        return (
          <div
            key={recommend.recommendType}
            data-recommend-type={recommend.recommendType}
            ref={refs[index]?.ref ?? null}
          >
            <RecommendGrid
              recommendList={recommend.results}
              title={recommend.title}
              recommendType={recommend.recommendType}
              bookmarkListUrl="/dcfront/bookmark/bookmarkList/"
              saveBookmarkRequestBody={{ device: 'PC' }}
              isWideCard
              onClickFavorite={handleClickFavorite}
              onClickThumbnail={createHandleClickRecommend(
                recommend.effectName
              )}
              onClickCardDetail={createHandleClickRecommend(
                recommend.effectName
              )}
              onClickSaved={undefined}
            />
          </div>
        );
      })}
    </section>
  );
};

const createJobDetailRecommendParams = (jid: string) => [
  {
    // あなたが応募した求人に近い求人
    recommendType: 'AWS_Recommend_oubo',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'AWS_Recommend_oubo_pc',
    conditionItems: jid,
    location: window.location.href,
  },
  {
    // 気になるリストに保存している求人
    recommendType: 'bookmark_list',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'bookmark_list_pc',
    conditionItems: jid,
    location: window.location.href,
  },
  {
    // 行動履歴からおすすめの求人のうちまだ見ていない求人, あなたが閲覧した求人
    recommendType: 'AWS_Recommend_not_etsuran,view_list',
    dispType: 'json',
    effectEventId: 'event70,event70',
    effectPosition: 'Rekome_pc,Recome_pc',
    effectName: 'AWS_Recommend_not_etsuran_pc,view_list_pc',
    conditionItems: jid,
    location: window.location.href,
  },
];

// TODO: ABテスト切り戻し時削除、全適用時 createJobDetailRecommendParams へマージする
const createJobDetailRecommendParamsForABTest = (jid: string) => [
  // 類似レコメンド
  {
    recommendType: 'AWS_Recommend_skilltag',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'AWS_Recommend_skilltag_pc',
    conditionItems: jid,
    location: window.location.href,
  },
  ...createJobDetailRecommendParams(jid),
];
